import { motion } from "framer-motion";
import { GraduationCap, MapPin, Shield, Users } from "lucide-react";

const highlights = [
  { icon: Shield, text: "Seguridad certificada para menores" },
  { icon: Users, text: "Acompanamiento profesional" },
  { icon: MapPin, text: "Destinos educativos en todo el pais" },
  { icon: GraduationCap, text: "Viajes de egresados y excursiones" },
];

export default function Estudiantil() {
  return (
    <section
      id="estudiantil"
      className="py-20 md:py-32 bg-[#0D1525] overflow-hidden"
      data-testid="estudiantil-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <span className="text-[#3DD9E8] text-sm font-bold uppercase tracking-[0.3em] font-body">
            Para los mas jovenes
          </span>
          <h2 className="font-heading text-4xl md:text-6xl text-white uppercase tracking-tight mt-3">
            Turismo Estudiantil
          </h2>
          <p className="text-gray-400 font-body text-base md:text-lg mt-4 max-w-2xl">
            Organizamos traslados para viajes estudiantiles, excursiones educativas y viajes de egresados. Destinos culturales, recreativos y de aventura en todo el pais.
          </p>
        </motion.div>

        {/* Features row */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 p-4 bg-[#151e32] border border-white/5"
                data-testid={`estudiantil-feature-${i}`}
              >
                <Icon className="text-[#3DD9E8] flex-shrink-0" size={20} />
                <span className="text-gray-300 font-body text-sm">{h.text}</span>
              </div>
            );
          })}
        </motion.div>

        {/* Image grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Catedral - large */}
          <motion.div
            className="md:col-span-2 relative overflow-hidden group"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img
              src="/assets/estudiantil-3.jpeg"
              alt="Alumnos en la Catedral de La Plata"
              className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1525]/70 to-transparent flex items-end p-6">
              <span className="font-heading text-xl text-white uppercase tracking-wide">
                Visitas culturales
              </span>
            </div>
          </motion.div>

          {/* Kayak */}
          <motion.div
            className="relative overflow-hidden group"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <img
              src="/assets/estudiantil-4.jpeg"
              alt="Kayak en el lago"
              className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1525]/70 to-transparent flex items-end p-6">
              <span className="font-heading text-xl text-white uppercase tracking-wide">
                Aventura y naturaleza
              </span>
            </div>
          </motion.div>

          {/* Temaiken */}
          <motion.div
            className="relative overflow-hidden group"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <img
              src="/assets/estudiantil-5.jpg"
              alt="Alumnos en Temaiken"
              className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1525]/70 to-transparent flex items-end p-6">
              <span className="font-heading text-xl text-white uppercase tracking-wide">
                Temaiken
              </span>
            </div>
          </motion.div>

          {/* Campamento - wide */}
          <motion.div
            className="md:col-span-2 relative overflow-hidden group"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <img
              src="/assets/estudiantil-6.jpg"
              alt="Campamento estudiantil con buses"
              className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1525]/70 to-transparent flex items-end p-6">
              <span className="font-heading text-xl text-white uppercase tracking-wide">
                Campamentos
              </span>
            </div>
          </motion.div>

          {/* Flota estudiantil */}
          <motion.div
            className="md:col-span-1 relative overflow-hidden group"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <img
              src="/assets/estudiantil-7.jpg"
              alt="Flota de buses estudiantiles"
              className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1525]/80 via-transparent to-transparent flex items-end p-6">
              <div>
                <span className="font-heading text-xl text-white uppercase tracking-wide">
                  Nuestra flota estudiantil
                </span>
                <a
                  href="#contacto"
                  className="block mt-3 clip-slant bg-[#D0162A] hover:bg-[#b01020] text-white font-bold uppercase tracking-wider py-3 px-8 text-xs transition-colors duration-200 text-center"
                  data-testid="estudiantil-cta"
                >
                  Consultar viaje estudiantil
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
